import React from 'react';
import { styles } from '../styles.ts';

interface GeminiCall {
  systemInstruction: string;
  prompt: string;
  response?: string;
}

interface SearchResultItem {
  id: string;
  name: string;
  type: string;
  dimensions: { width: number; length: number; height: number };
  price: string;
}

interface SearchLog {
  query: string;
  resultsCount: number;
  results: SearchResultItem[];
}

interface LogsAndCodeProps {
  logs: any[];
  generatedScript: string;
  geminiCalls: GeminiCall[];
  searchLogs?: SearchLog[];
}

export function LogsAndCode({ logs, generatedScript, geminiCalls = [], searchLogs = [] }: LogsAndCodeProps) {
  return (
    <section style={styles.card}>
      <h2 style={styles.sectionHeader}>Log Stream & Generated Logic</h2>
      
      <div style={styles.logsSplitGrid}>
        {/* Logs stream list */}
        <div>
          <h3 style={styles.subHeader}>Pipeline Events</h3>
          <div style={styles.logBox}>
            {logs.length === 0 ? (
              <div style={styles.emptyLogs}>No pipeline events logged. Trigger a layout generation to view status.</div>
            ) : (
              logs.map((log, idx) => (
                <div key={idx} style={styles.logItem}>
                  <div style={styles.logMeta}>
                    <span style={{ 
                      ...styles.logBadge, 
                      backgroundColor: log.stage === 'error' ? '#ef4444' : 
                                      log.stage === 'complete' ? 'var(--success)' : 'rgba(255,255,255,0.1)'
                    }}>
                      {log.stage.toUpperCase()}
                    </span>
                    <span style={styles.logTime}>{new Date(log.timestamp).toLocaleTimeString()}</span>
                  </div>
                  <div style={styles.logText}>{log.message}</div>
                  {log.data && (
                    <details style={styles.details}>
                      <summary style={styles.summary}>View raw payload data</summary>
                      <pre style={styles.json}>{JSON.stringify(log.data, null, 2)}</pre>
                    </details>
                  )}
                </div>
              ))
            )}
          </div>
        </div>

        {/* Compiled Python Placement script */}
        <div>
          <h3 style={styles.subHeader}>Generated Math Code</h3>
          <div style={styles.codeBox}>
            {generatedScript ? (
              <pre style={styles.scriptPre}>
                <code style={styles.python}>{generatedScript}</code>
              </pre>
            ) : (
              <div style={styles.emptyScript}>Python math placement calculations for layout update generated by Gemini will display here.</div>
            )}
          </div>
        </div>
      </div>

      {/* Typesense Catalog Search Results */}
      {searchLogs && searchLogs.length > 0 && (
        <div style={{ marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
          <h3 style={styles.subHeader}>Typesense Catalog Searches</h3>
          <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', marginTop: '12px' }}>
            {searchLogs.map((search, idx) => (
              <details key={idx} style={styles.accordion} open={idx === 0}>
                <summary style={{ ...styles.accordionHeader, listStyle: 'none' }}>
                  <div style={styles.accordionTitle}>
                    <span>🔍</span>
                    <span>Query: "{search.query}"</span>
                  </div>
                  <span style={{ ...styles.badge, backgroundColor: search.resultsCount > 0 ? '#2e7d32' : '#ef4444' }}>
                    {search.resultsCount} {search.resultsCount === 1 ? 'item' : 'items'} found
                  </span>
                </summary>
                <div style={styles.accordionContent}>
                  {search.results.length === 0 ? (
                    <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic' }}>
                      No matching furniture found in catalog for this query.
                    </div>
                  ) : (
                    <div style={{ overflowX: 'auto' }}>
                      <table style={styles.table}>
                        <thead>
                          <tr>
                            <th style={styles.th}>ID</th>
                            <th style={styles.th}>Name</th>
                            <th style={styles.th}>Type</th>
                            <th style={styles.th}>Dimensions (W × L × H)</th>
                            <th style={styles.th}>Price</th>
                          </tr>
                        </thead>
                        <tbody>
                          {search.results.map((item, itemIdx) => (
                            <tr key={itemIdx} style={styles.tr}>
                              <td style={{ ...styles.td, fontFamily: 'monospace', fontSize: '11px', color: 'var(--text-muted)' }}>
                                {item.id.substring(0, 8)}...
                              </td>
                              <td style={{ ...styles.td, fontWeight: 'bold' }}>{item.name}</td>
                              <td style={styles.td}>
                                <span style={styles.code}>{item.type}</span>
                              </td>
                              <td style={styles.td}>
                                {item.dimensions.width.toFixed(2)}m × {item.dimensions.length.toFixed(2)}m × {item.dimensions.height.toFixed(2)}m
                              </td>
                              <td style={{ ...styles.td, color: '#a78bfa', fontWeight: 'bold' }}>
                                {item.price}
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              </details>
            ))}
          </div>
        </div>
      )}

      {/* Gemini API Calls Prompt Inspector */}
      <div style={{ marginTop: '16px', borderTop: '1px solid var(--border-color)', paddingTop: '20px' }}>
        <h3 style={styles.subHeader}>Gemini API Request Inspector</h3>
        {geminiCalls.length === 0 ? (
          <div style={{ color: 'var(--text-muted)', fontSize: '13px', fontStyle: 'italic', padding: '10px 0' }}>
            No active API payloads inspected. Update layout to view prompt contexts.
          </div>
        ) : (
          <div style={styles.geminiPromptsBox}>
            {geminiCalls.map((call, idx) => (
              <div key={idx} style={styles.geminiCallCard}>
                <div style={{ fontSize: '12px', fontWeight: 'bold', color: 'var(--accent)', textTransform: 'uppercase', letterSpacing: '0.05em' }}>
                  Gemini API Request #{idx + 1}
                </div>
                
                <div>
                  <div style={{ ...styles.fieldLabel, marginBottom: '6px', fontSize: '10px' }}>System Instruction</div>
                  <pre style={styles.preWrap}>{call.systemInstruction}</pre>
                </div>
                
                <div>
                  <div style={{ ...styles.fieldLabel, marginBottom: '6px', fontSize: '10px' }}>User Prompt & Layout Context</div>
                  <pre style={styles.preWrap}>{call.prompt}</pre>
                </div>

                {call.response && (
                  <div>
                    <div style={{ ...styles.fieldLabel, marginBottom: '6px', fontSize: '10px', color: 'var(--success)' }}>AI Response</div>
                    <pre style={{ ...styles.preWrap, borderLeft: '3px solid var(--success)', paddingLeft: '8px' }}>{call.response}</pre>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
